import type { GetCrmConnectionUsageResponse } from '@otter-ai/recall-client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { OtterPlanType } from '@/shared/types/plan';
import { crmConnectionUsageQueryCreator } from '../query/crmConnectionUsageQueryCreator';

type CrmServiceResponseKey = keyof NonNullable<
  GetCrmConnectionUsageResponse['usage']
>;

export function useCrmPaywall(service: CrmServiceResponseKey) {
  const { adminStatus } = useWorkspace();
  const isCrmPaywallEnabled = useExperimentEnabled('crm_paywall');

  const { data: subscription } = useQuery(subscriptionQuery());
  const { data: crmConnectionUsage } = useQuery({
    ...crmConnectionUsageQueryCreator(),
    enabled: isCrmPaywallEnabled,
  });

  // Get usage and limit for the service
  const usageData = useMemo(() => {
    if (!isCrmPaywallEnabled || !crmConnectionUsage?.usage?.[service]) {
      return { usage: 0, limit: 0, remaining: 0 };
    }
    const { limit = 0, usage = 0 } = crmConnectionUsage.usage[service] || {};
    return { usage, limit, remaining: Math.max(0, limit - usage) };
  }, [isCrmPaywallEnabled, crmConnectionUsage, service]);

  // Determine if CRM connection limit is reached
  const isCrmLimitReached = useMemo(() => {
    if (!isCrmPaywallEnabled || !crmConnectionUsage?.usage?.[service])
      return false;
    const { limit, usage } = crmConnectionUsage.usage[service] || {};
    // If limit is null, it means the user has no connection limit
    if (usage === undefined || limit === undefined || limit === null)
      return false;
    return usage >= limit;
  }, [isCrmPaywallEnabled, crmConnectionUsage, service]);

  // Check if user is on a free plan (free users shouldn't see the paywall)
  const isFreePlan = useMemo(() => {
    const planType = subscription?.plan.type;
    return planType === OtterPlanType.FREE;
  }, [subscription?.plan.type]);

  const shouldShowConnectionsLeft = useMemo(() => {
    if (!isCrmPaywallEnabled || isCrmLimitReached || isFreePlan) return false;
    const { usage, limit } = usageData;
    return adminStatus?.isAdmin && usage >= 1 && limit > 1;
  }, [
    isCrmPaywallEnabled,
    isCrmLimitReached,
    isFreePlan,
    usageData,
    adminStatus?.isAdmin,
  ]);

  // Determine if paywall should be shown
  const shouldShowPaywall =
    !isFreePlan && (isCrmLimitReached || shouldShowConnectionsLeft);

  // Determine the paywall message key based on plan type and state
  const paywallMessageKey = useMemo(() => {
    if (shouldShowConnectionsLeft) {
      return 'adminConnectionsLeft' as const;
    }

    if (!isCrmLimitReached) return '';

    if (!adminStatus?.isAdmin) {
      return 'memberMessage' as const;
    }

    if (subscription?.plan.type === OtterPlanType.PREMIUM) {
      return 'proAdminMessage' as const;
    }

    return 'teamAdminMessage' as const;
  }, [
    shouldShowConnectionsLeft,
    isCrmLimitReached,
    adminStatus?.isAdmin,
    subscription?.plan.type,
  ]);

  // Show upgrade button only for Pro admins (in the paywall banner)
  const showUpgradeButton = paywallMessageKey === 'proAdminMessage';

  // Show contact sales button for Business (Team) admins
  const showContactSalesButton =
    paywallMessageKey === 'teamAdminMessage' ||
    paywallMessageKey === 'adminConnectionsLeft';

  return {
    isCrmLimitReached,
    shouldShowPaywall,
    paywallMessageKey,
    isCrmPaywallEnabled,
    connectionsRemaining: usageData.remaining,
    connectionsUsage: usageData.usage,
    showUpgradeButton,
    showContactSalesButton,
    isFreePlan,
  };
}
